import { format } from 'date-fns'
import { Selection } from '../enums'
import { TryParseInt } from '../Utils/parseInt'
import type {
  VehicleIdentificationDto as BaseVehicleIdentificationDto,
  ExtraEquipment,
  VehicleDto,
} from './Step1'

export class FindVehicle implements VehicleDto, BaseVehicleIdentificationDto {
  licensePlate?: string | null
  vin?: string | null
  druh?: number | null
  palivo?: number | null
  znacka?: string | null
  zakladniModel?: string | null
  model?: string | null
  modelBase?: string | null
  puvod?: number | null
  objem?: number | null
  vykon1?: number | null
  pocetMistSezeni?: number | null
  celkovaHmotnost?: number | null
  cisloTechnickehoPrukazu?: string | null
  cisloOrv?: string | null
  firstRegistrationDate?: Date | null
  firstRegistrationDateCz?: Date | null
  brokerMakeModelId?: number | null | undefined

  constructor(vinOnline: VinOnlineVehicleDto | null) {
    if (!vinOnline) return

    this.licensePlate = vinOnline.registrationMark
    this.vin = vinOnline.vin
    this.druh = TryParseInt(vinOnline.CategoryID?.toString(), null)
    this.palivo = TryParseInt(vinOnline.FuelID?.toString(), null)
    this.znacka = vinOnline.MakeBase?.replace('CITROEN', 'CITROËN')
    // stará WS VINONLINE: konkrétní model dodává enrichment (CarVehicleEnum/Uniqa), ModelText je základní model;
    // nový REST (poznáme podle BrokerMakeModelId): ModelText je konkrétní model z Broker číselníku
    this.model = vinOnline.modelCarEnum ?? vinOnline.modelUniqa ?? (vinOnline.BrokerMakeModelId != null ? vinOnline.ModelText : undefined)
    this.zakladniModel = vinOnline.ModelText?.replace('ŘADA', 'RADA')
    this.modelBase = vinOnline.ModelBase
    if (vinOnline.puvodVozidla)
      this.puvod =
        vinOnline.puvodVozidla?.toLocaleLowerCase() === 'o'
          ? Selection.VehicleOriginForeign
          : Selection.VehicleOriginCzech

    this.objem = vinOnline.CylinderVolume
    this.vykon1 = vinOnline.EnginePower
    this.pocetMistSezeni = vinOnline.SeatsCount
    this.celkovaHmotnost = vinOnline.TotalWeigth
    this.cisloTechnickehoPrukazu = vinOnline.TechnicalCertificateNumber
    this.cisloOrv = vinOnline.CisloOrv

    const datumPrvniRegistraceZahranici =
      vinOnline?.FirstRegistrationDateForeign
        ? vinOnline?.FirstRegistrationDateForeign
        : undefined
    const datumPrvniRegistrace = vinOnline?.FirstRegistrationDate
      ? vinOnline?.FirstRegistrationDate
      : undefined

    const foreignFirst =
      datumPrvniRegistraceZahranici && datumPrvniRegistrace
        ? datumPrvniRegistraceZahranici < datumPrvniRegistrace
        : false

    if (!this.puvod) {
      if (foreignFirst) {
        this.puvod = Selection.VehicleOriginForeign
      } else {
        this.puvod = Selection.VehicleOriginCzech // jako výchozí hodnotu ponechat původ ČR
      }
    }

    const firstRegistrationDate =
      datumPrvniRegistraceZahranici ?? datumPrvniRegistrace

    this.firstRegistrationDate = firstRegistrationDate
    this.firstRegistrationDateCz = datumPrvniRegistrace
    this.brokerMakeModelId = vinOnline.BrokerMakeModelId
  }
  subCategory?: number | null | undefined
  znackaText?: string | null | undefined
  vehicleTypeDescription?: string | null | undefined
  uzemniPlatnost?: number | null | undefined
  vykon2?: number | null | undefined
  isInProduction?: boolean | null | undefined
  mileage?: number | null | undefined
  usage?: number | null | undefined
  yearKm?: string | null | undefined
  serialNumber?: string | null | undefined
  purchaseDate?: Date | null | undefined
  warrantyStartDate?: Date | null | undefined
  warrantyKmLimit?: string | null | undefined
  warrantyTermLimit?: string | null | undefined
  manufacturerWarrantyType?: number | null | undefined
  vehicleLeasing: boolean | null = null
  smluvniServis: boolean | null = null
  prefLikvidace: boolean | null = null
  autorizovanyServis: boolean | null = null
  foreignRepairShop: boolean | null = null
  equipment: number[] | null = null
  extraEquipments: ExtraEquipment[] | null = null
  security: number[] | null = null
  vehicleGaraged?: boolean | undefined

  // uzemniPlatnost?: string | undefined
  // vykon2?: number | null | undefined
  //mileage?: number | null | undefined
  // usage?: number | undefined
  // yearKm?: string | undefined
  // purchaseDate?: Date | null | undefined
  // warrantyStartDate?: Date | null | undefined
  // warrantyKmLimit?: string | undefined
  // warrantyTermLimit?: string | undefined
  // extraEquipmentValue?: string | undefined
  // extraEquipmentDescription?: string | undefined
}

export interface VinOnlineVehicleDto {
  error?: string
  registrationMark?: string
  vin?: string
  MakeBase?: string
  FuelID?: number
  TotalWeigth?: number
  FirstRegistrationDateString?: string
  ModelText?: string
  ModelBase?: string
  CylinderVolume?: number
  SeatsCount?: number
  EnginePower?: number
  CategoryID?: number
  druh?: string
  TechnicalCertificateNumber?: string
  CisloOrv?: string
  FirstRegistrationDate?: Date
  FirstRegistrationDateForeign?: Date
  druhPaliva?: string
  puvodVozidla?: string
  vysledekKontrolyVin?: boolean
  vysledekKontrolyVinTextove?: string
  Generace?: string
  Verze?: string
  modelMerged?: string
  modelHint?: string
  modelCarEnum?: string
  makeUniqa?: string
  modelUniqa?: string
  BrokerMakeModelId?: number
}

export type VehiclesIdentificationDto = {
  vehicles?: OneVehicleIdentificationDto[]
  error?: string
}

export type OneVehicleIdentificationDto = {
  id?: string
  description?: string
  vin?: string
  registrationMark?: string
  typeDescription?: string
  accessoriesOptionalSelected?: string
  saleFrom?: Date
  saleTo?: Date
  versionSpecificationChanged?: string | null
  registrationDate?: Date | null
  price?: number
  priceWithEquipment?: number
  engine?: string
  fuel?: string
  transmission?: string
  consumption?: string
  brand?: string
  model?: string
  generation?: string
  versionName?: string
  pdfLink?: string
}

export type VehicleValuationDto = {
  baseVehicleNewPrice: number
  vehicleNewPrice: number
  baseVehicleMarketPrice: number
  vehicleMarketPrice: number
  vehicleMarketPriceWithTax: number
  error?: string
}

export type GetCebiGlassRequest = {
  licencePlate?: string | null
  vin?: string | null
}
export type CebiGlassInfoDto = {
  vin?: string
  registrationMark?: string
  PriceOriginal?: number
  PriceSecondaryManufacturing?: number
  PriceOriginalNoVat?: number
  PriceSecondaryManufacturingNoVat?: number
  error?: string
}

export type VehicleValuationModel = {
  paramsId?: string
  licensePlate: string
  vin: string
  vehicleId: string
  firstRegistrationDate: Date
  accSelected?: string
  mileage?: number | null
}

export const getVehicleInformationItems = (vinOnline: VinOnlineVehicleDto) => {
  const result = [
    { label: 'Registrační značka (SPZ)', value: vinOnline.registrationMark },
    { label: 'Číslo TP', value: vinOnline.TechnicalCertificateNumber },
    { label: 'Číslo ORV', value: vinOnline.CisloOrv },
    { label: 'VIN', value: vinOnline.vin },
    { label: 'Značka', value: vinOnline.MakeBase },
    { label: 'Model', value: vinOnline.modelMerged || vinOnline.ModelText },
    { label: 'Generace', value: vinOnline.Generace },
    { label: 'Verze', value: vinOnline.Verze },
    { label: 'Míst k sezení', value: vinOnline.SeatsCount },
    { label: 'Druh vozidla', value: vinOnline.druh },
    { label: 'Druh paliva', value: vinOnline.druhPaliva },
    { label: 'Celková hmotnost', value: vinOnline.TotalWeigth },
    { label: 'Výkon', value: vinOnline.EnginePower },
    { label: 'Objem', value: vinOnline.CylinderVolume },
  ]



  let puvod: typeof Selection.VehicleOriginForeign | typeof Selection.VehicleOriginCzech | undefined = undefined
  if (vinOnline.puvodVozidla) {
    const puvod = vinOnline.puvodVozidla?.toLocaleLowerCase() === 'o' ? Selection.VehicleOriginForeign : Selection.VehicleOriginCzech
    result.push({ label: 'Původ', value: puvod == Selection.VehicleOriginForeign ? 'v zahraničí' : 'v ČR' })
  }

  const datumPrvniRegistraceZahranici = vinOnline?.FirstRegistrationDateForeign ? vinOnline?.FirstRegistrationDateForeign : undefined
  const datumPrvniRegistrace = vinOnline?.FirstRegistrationDate ? vinOnline?.FirstRegistrationDate : undefined

  const foreignFirst = datumPrvniRegistraceZahranici && datumPrvniRegistrace
    ? datumPrvniRegistraceZahranici < datumPrvniRegistrace
    : false

  if (!puvod && foreignFirst)
    puvod = Selection.VehicleOriginForeign

  const firstRegistrationDate = datumPrvniRegistraceZahranici ?? datumPrvniRegistrace
  if (firstRegistrationDate)
    result.push({
      label: 'První registrace',
      value: puvod == Selection.VehicleOriginForeign ? format(new Date(vinOnline.FirstRegistrationDateForeign!), 'd. M. yyyy') : vinOnline.FirstRegistrationDateString
    })

  if (datumPrvniRegistrace)
    result.push({
      label: 'První registrace v ČR',
      value: puvod == Selection.VehicleOriginForeign ? format(new Date(vinOnline.FirstRegistrationDate!), 'd. M. yyyy') : undefined
    })

  return result
}